/**
 * Chat API – PHP CI4 backend
 * Set REACT_APP_CHAT_API_BASE or .env: REACT_APP_CHAT_API_BASE=http://localhost:8080
 */

const BASE_URL = process.env.REACT_APP_CHAT_API_BASE || '';

/**
 * @param {Object} [params]
 * @param {number} [params.id] - Current user id
 * @param {string} [params.role] - Current user role (e.g. 'staff' | 'student')
 * @returns {Promise<{ id: number, role: string, name: string, avatar: string|null }[]>}
 */
export async function fetchChatUsers(params = {}) {
    const base = BASE_URL;
    if (!base) throw new Error('REACT_APP_CHAT_API_BASE is not set');
    const {
        id,
        role,
        registrationId,
        roomId
    } = params;
    const res = await fetch(`${base}/users`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            id: id || null,
            role: role || null,
            registrationId: registrationId || null,
            roomId: roomId || null
        }),
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    const json = await res.json();
    if (!json.success || !Array.isArray(json.data)) return [];
    return json.data.map((u) => ({
        id: u.id,
        role: u.role,
        name: u.name,
        avatar: u.avatar || null,
    }));
}

/**
 * @param {Object} [params]
 * @param {string} [params.roomId] - Room id to fetch messages for
 * @param {number} [params.afterId] - Only messages with a larger id (incremental poll); omit for full history
 * @returns {Promise<{ id: number, userId: number, text: string, timestamp: string, replyToId: number|null, imageUrl: string|null, mentionedUserIds: number[] }[]>}
 */
export async function fetchChatMessages(params = {}) {
    const base = BASE_URL;
    if (!base) throw new Error('REACT_APP_CHAT_API_BASE is not set');
    const roomId = params.roomId ? params.roomId : null;
    const res = await fetch(`${base}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            roomId: roomId,
            ...(params.afterId > 0 ? { afterId: params.afterId } : {}),
        }),
    });
    if (!res.ok) throw new Error('Failed to fetch messages');
    const json = await res.json();
    if (!json.success || !Array.isArray(json.data)) return [];
    return json.data.map((m) => normalizeMessage(m));
}

/**
 * @param {Object} payload
 * @param {number} payload.sender_id
 * @param {string} payload.text
 * @param {number|null} [payload.reply_to_id]
 * @param {number[]} [payload.mentioned_user_ids]
 * @param {File|null} [payload.image]
 * @returns {Promise<{ id: number, userId: number, text: string, timestamp: string, replyToId: number|null, imageUrl: string|null, mentionedUserIds: number[] }>}
 */
export async function insertChatMessage(payload) {
    const base = BASE_URL;
    if (!base) throw new Error('REACT_APP_CHAT_API_BASE is not set');
    const senderId = payload.sender_id != null ? Number(payload.sender_id) : null;
    if (senderId == null || Number.isNaN(senderId)) throw new Error('sender_id is required');
    const hasImage = payload.image instanceof File;

    let res;
    if (hasImage) {
        const form = new FormData();
        form.append('sender_id', String(senderId));
        form.append('text', payload.text || '');
        form.append('roomId', payload.room_id || '');
        form.append('reply_to_id', payload.reply_to_id != null ? String(payload.reply_to_id) : '');
        form.append('mentioned_user_ids', JSON.stringify(Array.isArray(payload.mentioned_user_ids) ? payload.mentioned_user_ids : []));
        form.append('image', payload.image);
        res = await fetch(`${base}/insertMessage`, {
            method: 'POST',
            body: form,
        });
    } else {
        const body = {
            sender_id: senderId,
            text: payload.text || '',
            roomId: payload.room_id || '',
            reply_to_id: payload.reply_to_id != null ? Number(payload.reply_to_id) : null,
            mentioned_user_ids: Array.isArray(payload.mentioned_user_ids) ? payload.mentioned_user_ids : [],
        };
        console.log('body', body);
        res = await fetch(`${base}/insertMessage`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body),
        });
    }
    let json;
    try {
        json = await res.json();
    } catch (_) {
        throw new Error(res.ok ? 'Invalid response from server' : `Request failed: ${res.status} ${res.statusText}`);
    }
    if (!json.success) throw new Error(json.message || json.error || 'Failed to send message');
    const m = json.data;
    if (!m) throw new Error('No message data in response');
    return normalizeMessage(m);
}

function normalizeMessage(m) {
    return {
        id: m.id || m.message_id,
        userId: m.userId || m.user_id || m.sender_id,
        studentUserId: m.studentUserId || m.student_user_id || null,
        name: m.name || m.user_name || null,
        text: m.text || m.message || '',
        timestamp: m.timestamp || m.created_at || m.created || new Date().toISOString(),
        replyToId: m.replyToId || m.reply_to_id || null,
        imageUrl: m.imageUrl || m.image_url || null,
        mentionedUserIds: Array.isArray(m.mentionedUserIds) || Array.isArray(m.mentioned_user_ids) ? m.mentionedUserIds || m.mentioned_user_ids : [],
    };
}